/**
 * ── ⭐ WORLD READINESS — "LOADED" IS NOT "PLAYABLE", AND THE SIM MUST WAIT FOR THE SECOND ────────
 *
 * The owner: *"the worlds are loaded before the static meshes, which means that the level starts
 * with no collision."* He is right, and the gap is measurable: `host.loadAllIndexed` returns when
 * every entity has been CONSTRUCTED, `staticMesh.create()` returns synchronously and adds its GLB
 * inside a `.then()`, and `mount`'s `bindCollision` waits on `inst.ready` in a fire-and-forget
 * `.then()`. So the animation loop starts, the fixed-tick accumulator runs, and the player
 * integrates gravity against an EMPTY physics world — on Sponza for the **2.9 s** its 103 trimeshes
 * take to extract and bind (measured, `state().collision.entities[0].buildMs`).
 *
 * ── WHY THIS IS A MODULE AND NOT A BOOLEAN IN `mount.ts` ────────────────────────────────────────
 *
 * "Is the world playable" is a PREDICATE OVER A SET, and writing it as "have all entities loaded"
 * is the one choice that would make cell streaming a rewrite later. Today the required set is
 * every placement that expects to carry collision; when the world is partitioned it becomes the
 * cells overlapping the spawn, and nothing else here has to change. The set is the seam.
 *
 * ⚠️ AND THE PREDICATE IS ABOUT PHYSICS, NOT ABOUT ASSETS. `inst.ready` resolving means the GLB
 * arrived and the shapes were extracted; it does NOT mean Rapier holds a collider — the bind is a
 * separate async hop through `setEntityCollision`, and that hop is exactly where the bug lives.
 * `host.onAllLoaded()` (which is `onAllSpawned` + `allSettled` over every `ready`) is therefore
 * NECESSARY AND NOT SUFFICIENT, and gating on it alone would look right and still let the player
 * fall.
 *
 * ⚠️ AND IT MUST BE ABLE TO GIVE UP. `staticMesh`'s trimesh extraction yields on
 * `requestAnimationFrame` between primitives, and rAF does not fire while the tab is hidden — so a
 * world opened in a background tab leaves `ready` pending forever. A gate with no deadline turns
 * a 3-second bug into a boot that never finishes. `residency()` reports; the CALLER owns the
 * deadline and what it does when the deadline passes (mount: warn once, start anyway — rule 6,
 * an unimplemented promise warns and never hangs).
 *
 * PURE (`PURE_FILES`): no three, no Rapier, no DOM, no clock. It folds two lists into a verdict.
 */

import {
  resolveCollisionMode,
  type AssetCollisionMode,
  type PlacementCollisionMode,
} from './collision';
import { resolveEntityDef } from './prefabs';
import type { PrefabTable } from './prefabs';

/** The asset manifest rows readiness needs — the same shape `resolveCollisionMode` reads. */
export interface ReadinessAsset {
  id: string;
  kind: string;
  collision?: AssetCollisionMode | undefined;
}

/** One placement that MUST hold colliders before the sim may run, and why. */
export interface RequiredResident {
  index: number;
  /** The resolved base class — prefabs resolved, never the authored `type`. */
  baseType: string;
  /** The asset id it draws, when it names one. */
  asset: string;
  /** The resolved collision mode. `none` never appears here — those are not required. */
  mode: AssetCollisionMode;
}

/** What `residency()` answers. Flat and serializable so the dev hook can publish it. */
export interface ResidencyReport {
  /** Placements that expect colliders, in world order. */
  required: readonly RequiredResident[];
  /** Of those, how many the physics world actually holds colliders for. */
  bound: number;
  /** Indices STILL COMING, in world order — what a loading line would name. */
  missing: readonly number[];
  /**
   * ⭐ L3.B — indices whose load has SETTLED and which bound NOTHING. Not "missing": never coming.
   *
   * The bug this exists to fix costs twenty seconds and is invisible in every test we had.
   * `staticMesh` handles a dead asset properly — `showPlaceholder('load failed …')`, `ready`
   * RESOLVES (it never rejects), `inst.collision` stays null — so `bindCollision` calls
   * `setEntityCollision(index, null, …)`, the index binds ZERO colliders, the caller's
   * `colliders > 0` filter never lists it, `resident` never goes true, and THE 20 s DEADLINE is
   * what starts the level. One missing crate, twenty seconds of a frozen world.
   *
   * Separating "still coming" from "came back with nothing" is what makes the fix expressible at
   * all, and it is why the phase model is a model and not a boolean.
   */
  broken: readonly number[];
  /** `true` when nothing is still coming — bound, or settled-and-broken. The gate. */
  resident: boolean;
}

/**
 * Which placements the sim must not start without.
 *
 * A `player_start` carries no collision and must never hold the gate open; a `static_mesh` whose
 * resolved mode is `none` is an artist saying "walk through this", which is a decision and not a
 * missing asset. Both are excluded, so the gate opens on a world that legitimately has no solid
 * geometry rather than hanging on one that never intended any.
 *
 * ⚠️ PREFABS ARE RESOLVED. A raw `def.type !== 'static_mesh'` compare — which is what
 * `worldQuery.unseenSolidCount` still does — misses a crate placed through a prefab that resolves
 * to `static_mesh`, and a gate that cannot see half the level is worse than no gate: it reports
 * ready while the floor is still arriving.
 */
export function requiredResidents(
  entities: readonly unknown[],
  prefabs: PrefabTable,
  assets: readonly ReadinessAsset[],
): RequiredResident[] {
  const byId = new Map(assets.map((a) => [a.id, a]));
  const out: RequiredResident[] = [];
  for (let index = 0; index < entities.length; index++) {
    const resolved = resolveEntityDef(entities[index], prefabs);
    if (resolved === null || resolved.baseType !== 'static_mesh') continue;
    const def = resolved.def as { asset?: unknown; collision?: unknown };
    const asset = typeof def.asset === 'string' ? def.asset : '';
    const { mode } = resolveCollisionMode(byId.get(asset), {
      // A PLACEMENT's vocabulary is narrower than an ASSET's (`asset | trimesh | box | none`;
      // `ucx` is a thing an asset declares, never a thing a placement picks). Reading it as the
      // asset's type would let a nonsense value through the resolver's `includes` guard.
      collision: def.collision as PlacementCollisionMode | undefined,
    });
    if (mode === 'none') continue;
    out.push({ index, baseType: resolved.baseType, asset, mode });
  }
  return out;
}

/**
 * Fold the required set against what physics actually holds.
 *
 * `boundIndices` is the caller's read of the PHYSICS WORLD (`entityCollisionInfo()` filtered to
 * `colliders > 0`), never a self-report from the loader — "the asset arrived" and "Rapier holds a
 * collider" are the two facts this whole module exists to keep apart.
 */
export function residency(
  required: readonly RequiredResident[],
  boundIndices: Iterable<number>,
  settledIndices?: Iterable<number>,
): ResidencyReport {
  const bound = new Set(boundIndices);
  /*
   * ⚠️ OMITTED ⇒ "settled means bound", which is EXACTLY the pre-L3.B behaviour, so every caller
   * that has not learned about settlement keeps the answer it always got.
   *
   * Written as an optional parameter rather than the spec's `settledIndices = boundIndices`
   * default: a default of that shape re-iterates the caller's iterable a second time, and an
   * `Iterable<number>` may be a one-shot iterator (a generator, a `Set.values()`), in which case
   * the second walk yields NOTHING — every required index would read as unsettled and the report
   * would be silently correct only for callers who happened to pass an array. See §5.
   */
  const settled = settledIndices === undefined ? bound : new Set(settledIndices);
  const missing: number[] = [];
  const broken: number[] = [];
  let boundCount = 0;
  for (const r of required) {
    if (bound.has(r.index)) boundCount++;
    else if (settled.has(r.index)) broken.push(r.index);
    else missing.push(r.index);
  }
  return {
    required,
    bound: boundCount,
    missing,
    broken,
    // The gate opens on bound-OR-BROKEN. A placement that came back with nothing is a hole in the
    // floor the player can be told about (L4's `loadingPartial`); it is not a reason to hold a
    // whole level closed for the deadline's twenty seconds.
    resident: missing.length === 0,
  };
}

/** One line for a loading surface or a console warning. Never a stack, never a path. */
export function describeResidency(r: ResidencyReport): string {
  if (r.required.length === 0) return 'no solid geometry to wait for';
  // The broken clause rides on EVERY line, resident or not: "3/5 bound" with two placements that
  // are never coming reads as healthy progress, which is the one way this sentence could lie.
  const hole = r.broken.length > 0 ? ` — ${r.broken.length} broken` : '';
  if (r.resident) return `${r.bound}/${r.required.length} solid placements bound${hole}`;
  const first = r.required.find((x) => x.index === r.missing[0]);
  const what = first === undefined ? '' : ` — waiting on ${first.asset || first.baseType}`;
  return `${r.bound}/${r.required.length} solid placements bound${what}${hole}`;
}

/**
 * ── ⭐ P1 · THE LIBRARY'S ONE COUNTER (ADR-0003 §4.3, plan §9's P1 gate) ─────────────────────────
 *
 * The asset library adds exactly ONE rung's worth of arithmetic to this module and to
 * `loadPhase.ts`, and this is it: **boot-lane library handles must be resident before `playing`**.
 * Gameplay and prefetch lanes never gate — that is the whole difference between a boot request and
 * a mid-play summon, and it is why a summoned entity gets a rung without joining the boot ladder.
 *
 * ⚠️ IT TAKES DATA, NOT A SERVICE. `readiness.ts` is PURE (`PURE_FILES`) and may not import
 * `src/assets/**`; mount projects `AssetService.bootGate()` — itself `assets/core/readiness.ts`'s
 * pure fold over the live handles — into this flat shape. Two folds, one direction, and the seam
 * between them is four integers. That is also what keeps the predicate assertable here with no
 * service at all.
 *
 * ⚠️ AND `broken` DOES NOT HOLD THE GATE, for exactly the reason `ResidencyReport.broken` does not:
 * a boot handle that settled `failed` is never coming, and holding a level closed for the 20 s
 * deadline over one 404'd clip is the bug L3.B already paid for once on the collision side. The
 * character keeps its capsule, the companion keeps its hull, and the level starts.
 */
export interface LibraryResidency {
  /** Distinct boot-lane asset ids this session has acquired. */
  required: number;
  /** …of those, ready (or released after being ready). */
  resident: number;
  /** …settled `failed`. Never coming; the placeholder policy shows instead. */
  broken: number;
  /** …still `queued`/`fetching`/`parsing`. THE gate: `playing` waits on this reaching zero. */
  pending: number;
}

/** No library this session (no service, or no boot acquire yet) reads as nothing to wait for. */
export function libraryResidency(
  report: { required: readonly string[]; pending: readonly string[]; broken: readonly string[]; resident: readonly string[] } | null,
): LibraryResidency | null {
  if (report === null) return null;
  return {
    required: report.required.length,
    resident: report.resident.length,
    broken: report.broken.length,
    pending: report.pending.length,
  };
}

/**
 * THE gate, both halves. Colliders bound (or settled-and-broken) AND nothing in the library's boot
 * lane still coming. `null` for the library means this session has none — which is every world
 * that is not exploration and every runtime built before ADR-0003 — and reads as satisfied.
 */
export function worldPlayable(world: ResidencyReport, library: LibraryResidency | null): boolean {
  return world.resident && (library === null || library.pending === 0);
}

/** One clause for the console line and the loading surface; `''` when there is nothing to say. */
export function describeLibraryResidency(lib: LibraryResidency | null): string {
  if (lib === null || lib.required === 0) return '';
  const hole = lib.broken > 0 ? `, ${lib.broken} broken` : '';
  return `${lib.resident}/${lib.required} boot assets resident${hole}`;
}
